class CartPromotionProgress extends HTMLElement {
  constructor() {
    super()
    this.init()
  }

  init() {
    if (!window.cartPromotions || !Array.isArray(window.cartPromotions) || window.cartPromotions.length === 0) {
      this.hide()
      return
    }

    this.promotions = window.cartPromotions.sort((a, b) => a.priority - b.priority)
    this.messageEl = this.querySelector('[data-promotion-message]')
    this.barEl = this.querySelector('[data-promotion-bar]')
    this.rewardsEl = this.querySelector('[data-promotion-rewards]')
    this.loadingEl = this.querySelector('[data-promotion-loading]')

    this.update()
    this.setupEventListeners()
  }

  // 绑定监听
  setupEventListeners() {
    // Listen for cart updates
    document.addEventListener('cart:updated', () => {
      this.update()
    })

    // Listen for cart drawer updates
    document.addEventListener('cart:drawer:updated', () => {
      this.update()
    })

    // Listen for custom promotion update event
    document.addEventListener('cart:promotion:update', () => {
      this.update()
    })
  }

  async getCartData() {
    try {
      const response = await fetch('/cart.js')
      if (!response.ok) throw new Error('Failed to fetch cart')
      return await response.json()
    } catch (error) {
      console.error('Error fetching cart:', error)
      return null
    }
  }

  calculateProgress(currentValue, threshold) {
    if (!threshold || threshold <= 0) return 0
    return Math.min(currentValue / threshold, 1)
  }

  getCurrentValue(promotion, cartData) {
    if (!cartData) {
      cartData = window.Shopify?.cart || {}
    }
    
    // Filter out gift items to calculate accurate cart value
    const nonGiftItems = (cartData.items || []).filter(item => 
      item.properties?._gift !== 'true'
    )
    
    if (promotion.condition_type === 'quantity') {
      // Calculate quantity excluding gifts
      const nonGiftQuantity = nonGiftItems.reduce((total, item) => total + item.quantity, 0)
      return nonGiftQuantity
    } else if (promotion.condition_type === 'amount' || promotion.condition_type === 'anount') {
      // Calculate total price excluding gifts
      const nonGiftTotal = nonGiftItems.reduce((total, item) => {
        return total + (item.final_line_price || item.line_price || 0)
      }, 0)
      return nonGiftTotal / 100
    }
    return 0
  }

  getNextUnlockedReward(cartData) {
    for (const promotion of this.promotions) {
      const currentValue = this.getCurrentValue(promotion, cartData)
      const progress = this.calculateProgress(currentValue, promotion.threshold)
      
      if (progress < 1) {
        return {
          promotion,
          currentValue,
          remaining: promotion.threshold - currentValue,
          progress
        }
      }
    }
    return null
  }

  async update() {
    this.showLoading()

    try {
      const cartData = await this.getCartData()
      if (!cartData) {
        this.hide()
        return
      }

      // Update global cart data
      if (window.Shopify) {
        window.Shopify.cart = cartData
      }

      // Calculate progress for each reward independently
      let nextReward = null
      const rewardProgresses = []

      for (let i = 0; i < this.promotions.length; i++) {
        const promotion = this.promotions[i]
        const currentValue = this.getCurrentValue(promotion, cartData)
        const progress = this.calculateProgress(currentValue, promotion.threshold)
        const isUnlocked = progress >= 1
        
        rewardProgresses.push({
          promotion,
          progress,
          isUnlocked,
          index: i
        })

        if (!nextReward && !isUnlocked) {
          nextReward = {
            promotion,
            currentValue,
            remaining: Math.max(0, promotion.threshold - currentValue),
            index: i
          }
        }
      }

      // Calculate overall progress - cumulative/progressive segments
      // Each reward gets equal segment width
      if (this.promotions.length > 0) {
        const segmentWidth = 100 / this.promotions.length
        let totalProgress = 0
        
        // Progressive calculation: once a threshold is reached, move to next segment
        for (let i = 0; i < rewardProgresses.length; i++) {
          const rewardProgress = rewardProgresses[i]
          
          if (rewardProgress.isUnlocked) {
            // This segment is fully completed
            totalProgress += segmentWidth
          } else {
            // This is the current segment in progress
            totalProgress += rewardProgress.progress * segmentWidth
            // Stop here - don't add progress for segments we haven't reached yet
            break
          }
        }

        // Update progress bar
        if (this.barEl) {
          const progressPercent = Math.min(totalProgress, 100)
          this.barEl.style.width = `${progressPercent}%`
        }
      }

      // Update message
      if (this.messageEl) {
        if (nextReward) {
          const { promotion, remaining } = nextReward
          let remainingText = ''
          
          if (promotion.condition_type === 'quantity') {
            remainingText = `Spend ${Math.ceil(remaining)} more`
          } else if (promotion.condition_type === 'amount' || promotion.condition_type === 'anount') {
            remainingText = `Spend $${remaining.toFixed(2)} more`
          }
          
          this.messageEl.textContent = `${remainingText} to get ${promotion.title}`
        } else {
          // All rewards unlocked
          const unlockedRewards = rewardProgresses.filter(r => r.isUnlocked)
          if (unlockedRewards.length === this.promotions.length) {
            this.messageEl.textContent = `Congratulations! You've unlocked all rewards!`
          }
        }
      }


      this.renderRewards(cartData)

      await this.autoAddGifts(cartData)

      await this.autoRemoveGifts(cartData)

      this.hideLoading()
    } catch (error) {
      console.error('Error updating cart promotion:', error)
      this.hideLoading()
    }
  }

  renderRewards(cartData) {
    if (!this.rewardsEl) return

    this.rewardsEl.innerHTML = ''

    for (let i = 0; i < this.promotions.length; i++) {
      const promotion = this.promotions[i]
      const currentValue = this.getCurrentValue(promotion, cartData)
      const progress = this.calculateProgress(currentValue, promotion.threshold)
      const isUnlocked = progress >= 1
      
      // Check if this is the current reward (the first one not unlocked)
      let isCurrent = false
      if (!isUnlocked) {
        // Check if all previous rewards are unlocked
        let allPreviousUnlocked = true
        for (let j = 0; j < i; j++) {
          const prevValue = this.getCurrentValue(this.promotions[j], cartData)
          const prevProgress = this.calculateProgress(prevValue, this.promotions[j].threshold)
          if (prevProgress < 1) {
            allPreviousUnlocked = false
            break
          }
        }
        isCurrent = allPreviousUnlocked
      }

      // Check if gift is already in cart
      const giftInCart = promotion.reward_type === 'gift' && 
        cartData.items?.some(item => 
          item.properties?._gift === 'true' && 
          item.properties?._promo === promotion.title
        )

      const rewardEl = document.createElement('div')
      // Only add unlocked class if truly unlocked, current class is separate
      let rewardClassName = 'cart-promotion-progress__reward'
      if (isUnlocked) {
        rewardClassName += ' cart-promotion-progress__reward--unlocked'
      } else if (isCurrent) {
        rewardClassName += ' cart-promotion-progress__reward--current'
      }
      rewardEl.className = rewardClassName
      rewardEl.setAttribute('data-reward-index', i)
      rewardEl.setAttribute('data-reward-type', promotion.reward_type)
      
      // Position the reward icon based on its segment in the progress bar
      // Each reward is positioned at the end of its segment
      const segmentWidth = 100 / this.promotions.length
      const position = segmentWidth * (i + 1)
      rewardEl.style.left = `${position}%`
      
      if (promotion.reward_type === 'gift' && promotion.gift_variant) {
        rewardEl.setAttribute('data-gift-variant', promotion.gift_variant)
      }

      // Icon
      const iconEl = document.createElement('div')
      iconEl.className = 'cart-promotion-progress__reward-icon'
      
      let iconSvg = ''
      if (promotion.reward_type === 'percentage') {
        iconSvg = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="6" width="18" height="12" rx="2"/><path d="M7 10h.01M7 14h.01M17 10h.01M17 14h.01"/><rect x="7" y="10" width="10" height="4" rx="1"/></svg>'
      } else if (promotion.reward_type === 'gift') {
        iconSvg = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="8" width="18" height="4" rx="1"/><path d="M12 8v13M3 12v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5"/></svg>'
      } else {
        iconSvg = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>'
      }
      
      iconEl.innerHTML = iconSvg
      rewardEl.appendChild(iconEl)

      // Title
      const titleEl = document.createElement('div')
      titleEl.className = 'cart-promotion-progress__reward-title'
      titleEl.textContent = promotion.title
      rewardEl.appendChild(titleEl)

      this.rewardsEl.appendChild(rewardEl)
    }
  }

  async autoAddGifts(cartData) {
    for (const promotion of this.promotions) {
      if (promotion.reward_type !== 'gift' || !promotion.gift_variant) continue

      const currentValue = this.getCurrentValue(promotion, cartData)
      const progress = this.calculateProgress(currentValue, promotion.threshold)

      // Check if threshold is met and gift not already in cart
      if (progress >= 1) {
        const giftInCart = cartData.items?.some(item => 
          item.properties?._gift === 'true' && 
          item.properties?._promo === promotion.title
        )

        if (!giftInCart) {
          await this.addGiftToCart(promotion)
        }
      }
    }
  }

  async autoRemoveGifts(cartData) {
    for (const promotion of this.promotions) {
      if (promotion.reward_type !== 'gift' || !promotion.gift_variant) continue

      const currentValue = this.getCurrentValue(promotion, cartData)
      const progress = this.calculateProgress(currentValue, promotion.threshold)

      // Check if threshold is NOT met and gift is in cart
      if (progress < 1) {
        const giftItems = cartData.items?.filter(item => 
          item.properties?._gift === 'true' && 
          item.properties?._promo === promotion.title
        ) || []

        // Remove all gift items for this promotion
        for (const giftItem of giftItems) {
          await this.removeGiftFromCart(giftItem.key || giftItem.id)
        }
      }
    }
  }

  async addGiftToCart(promotion) {
    try {
      // Extract variant ID from GID
      const variantId = promotion.gift_variant.replace('gid://shopify/ProductVariant/', '')

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          properties: {
            _gift: 'true',
            _promo: promotion.title
          }
        })
      })

      if (!response.ok) {
        throw new Error('Failed to add gift to cart')
      }

      // Trigger cart update event
      document.dispatchEvent(new CustomEvent('cart:updated'))
      
      // If cart drawer is open, trigger its update
      const cartDrawer = document.querySelector('cart-drawer')
      if (cartDrawer) {
        document.dispatchEvent(new CustomEvent('cart:drawer:updated'))
      }
    } catch (error) {
      console.error('Error adding gift to cart:', error)
    }
  }

  async removeGiftFromCart(lineItemKey) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: lineItemKey,
          quantity: 0
        })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.description || 'Failed to remove gift from cart')
      }

      // Trigger cart update event
      document.dispatchEvent(new CustomEvent('cart:updated'))
      
      // If cart drawer is open, trigger its update
      const cartDrawer = document.querySelector('cart-drawer')
      if (cartDrawer) {
        document.dispatchEvent(new CustomEvent('cart:drawer:updated'))
      }
    } catch (error) {
      console.error('Error removing gift from cart:', error)
    }
  }

  // 购物车更新是动画
  showLoading() {
    if (this.loadingEl) {
      this.loadingEl.classList.remove('hidden')
    }
    if (this.rewardsEl) {
      // this.rewardsEl.style.opacity = '0.5'
    }
  }

  hideLoading() {
    if (this.loadingEl) {
      this.loadingEl.classList.add('hidden')
    }
    if (this.rewardsEl) {
      // this.rewardsEl.style.opacity = '1'
    }
  }

  hide() {
    this.style.display = 'none'
  }

  show() {
    this.style.display = 'block'
  }
}

customElements.define('cart-promotion-progress', CartPromotionProgress)

// Global helper function to trigger cart promotion update
window.updateCartPromotion = function() {
  document.dispatchEvent(new CustomEvent('cart:promotion:update'))
}

// Ensure initialization when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    // Elements will auto-initialize via customElements.define
  })
} else {
  // DOM already loaded, elements will auto-initialize
}
